import { Stack, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useMemo } from 'react';

import CollapsibleSection from 'components/v2/accordion/CollapsibleSection';
import OrderItem from 'components/v2/text/OrderItem';
import { CurrencyCode } from 'constants/enums/general';
import { useLocalization } from 'localization';
import { IProductDto } from 'api/products/dto';
import NewPackageSection from './NewPackageSection';
import NewProductSection from './NewProductSection';
import { calculateSectionPrice } from 'helpers/calculateSectionPrice';
import useGetKeyPickUpFee from 'hooks/services/useGetKeyPickUpFee';
import { ProductStructure } from 'constants/enums/products';
import { useAuth } from 'modules/auth/hooks';
import hasBookPhotographerTask from 'helpers/hasBookPhotographerTask';

const NewPreviewReceipt = () => {
  const { t, f } = useLocalization();

  const basicPackage = useWatch({ name: 'basicPackage' });
  const keyPickup = useWatch({ name: 'keyPickup' });
  const bundlePackagesProduct = useWatch({ name: 'bundlePackagesProduct' });
  const singleProducts = useWatch({ name: 'singleProducts' }) || {};
  const { user } = useAuth();
  const showPrice = user?.visiblePrices;

  const packageCurrency = CurrencyCode.DKK;
  const keyPickUpFee = useGetKeyPickUpFee();

  const groupedBundlePackages = useMemo(() => {
    if (!bundlePackagesProduct) return {};

    type Grouped = Record<
      string,
      {
        categoryId: string;
        rows: IProductDto[];
      }
    >;
    const result: Grouped = {};

    for (const [categoryId, items] of Object.entries(bundlePackagesProduct)) {
      const rows = Array.isArray(items) ? (items as IProductDto[]) : [];
      for (const item of rows) {
        const displayCategory = item.displayCategory ?? 'Unknown';

        if (!result[displayCategory]) {
          result[displayCategory] = {
            categoryId,
            rows: [],
          };
        }
        result[displayCategory].rows.push(item);
      }
    }

    return result;
  }, [bundlePackagesProduct]);

  const groupedSingles = useMemo(() => {
    const result: Record<string, { displayCategory: string; rows: IProductDto[] }> = {};

    for (const categoryId of Object.keys(singleProducts)) {
      const rows = singleProducts[categoryId] ?? [];
      if (!rows.length) continue;

      const filtered = rows.filter((p: IProductDto) => (p.quantity ?? 0) > 0);
      if (!filtered.length) continue;

      const displayCategory = filtered[0].displayCategory || '';

      result[categoryId] = {
        displayCategory,
        rows: filtered,
      };
    }

    return result;
  }, [singleProducts]);

  return (
    <Stack spacing={1.25}>
      {/* Basic product */}
      <CollapsibleSection
        title={<Typography variant="h6">Basis</Typography>}
        subTitle={
          showPrice ? `${f(basicPackage?.discountedPrice ?? 0, { currency: packageCurrency, decimals: 2 })}` : ''
        }
        defaultOpen={false}
      >
        {basicPackage?.children?.length ? (
          <>
            <Typography>{basicPackage?.name}</Typography>
            {basicPackage.children.map((item: IProductDto, index: number) => (
              <OrderItem
                visiblePrice={showPrice}
                key={index}
                name={item?.name ?? ''}
                quantity={item?.quantity ?? 0}
                price={
                  showPrice ? `(${f(item?.discountedPrice ?? 0, { currency: packageCurrency, decimals: 2 })})` : ''
                }
              />
            ))}
          </>
        ) : null}
      </CollapsibleSection>

      {Object.entries(groupedBundlePackages).map(([displayCategory, group]) => {
        if (!group.rows.length) return null;
        return (
          <NewPackageSection
            key={displayCategory}
            packages={group.rows}
            title={<Typography variant="h6">{displayCategory}</Typography>}
            subTitle={
              showPrice
                ? `${f(calculateSectionPrice(group.rows, ProductStructure.BundleProduct) ?? 0, { currency: packageCurrency, decimals: 2 })}`
                : ''
            }
          />
        );
      })}

      {Object.values(groupedSingles).map(group => (
        <NewProductSection
          key={group.displayCategory}
          products={group.rows}
          title={<Typography variant="h6">{group.displayCategory}</Typography>}
          subTitle={
            showPrice
              ? f(calculateSectionPrice(group.rows, ProductStructure.SingleProduct) ?? 0, {
                  currency: packageCurrency,
                  decimals: 2,
                })
              : ''
          }
        />
      ))}

      {/* Other fees such as key pickup fee */}
      {keyPickUpFee?.name && keyPickup && hasBookPhotographerTask() ? (
        <CollapsibleSection
          title={<Typography variant="h6">{t('common.terms.otherFees')}</Typography>}
          subTitle={`${f(keyPickUpFee?.price ?? 0, { currency: packageCurrency, decimals: 2 })}`}
        >
          <OrderItem key={keyPickUpFee?.name} name={keyPickUpFee?.name ?? ''} quantity={keyPickUpFee?.quantity} />
        </CollapsibleSection>
      ) : null}
    </Stack>
  );
};

export default NewPreviewReceipt;
